import type { ComputedRef, Ref } from 'vue';
import type { MessageApi } from 'naive-ui';
import type {
  BlendPartialCondition,
  BlendPreviewGuide,
  ChamferFeatureDefinition,
  ChamferType,
  FilletFeatureDefinition,
  FilletType,
  VariableRadiusControlPoint,
} from '@puppycad/cad-core';
import {
  canSwitchChamferTypeDuringEdit,
  normalizeFilletDefinitionForFaceFilletMode,
  normalizeOffsetFaceChamferDefinition,
} from '@puppycad/cad-core';
import {
  clonePlainData,
  describeBlendSelectionTarget,
  normalizeChamferDefinitionForFaceFaceMode,
  normalizeChamferDefinitionForTypeSwitch,
  normalizeFilletDefinitionForTypeSwitch,
} from '@/core/features/blend/command-utils';
import {
  createDefaultChamferDefinition,
  DEFAULT_CHAMFER_DISTANCE,
  normalizeConstantFilletDefinition,
} from '@/core/features/blend/defaults';
import { isConstantFilletEdgeModeLocked } from '@/core/features/blend/fillet-selection-policy';
import {
  assignVariableRadiusToAllPoints,
  clampVariableRadiusInstanceCount,
  DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
  ensureVariableRadiusEndpointPoints,
  parseVariableRadiusInstanceCandidateKey,
  removeAllVariableRadiusInstancePoints,
  removeVariableRadiusInstancePoint,
  resolveSelectedVariableRadiusPointKey,
  updateVariableRadiusPointRadius,
  upsertVariableRadiusInstancePointWithSelection,
  type VariableRadiusValueSlot,
} from '@/core/features/blend/variable-radius';
import {
  clearPartialEdgeReference,
  ensurePartialEdgeParams,
  resolvePartialReferenceActivationAfterConditionChange,
  setPartialEdgeParametersEnabled,
  updatePartialEdgeCondition,
  updatePartialEdgeValue,
  type BlendPartialReferenceTarget,
} from '@/core/features/blend/partial-edge';
import {
  removeFaceSetSelection,
  resolveDefaultFilletFaceSetId,
  type ChamferSelectionTarget,
  type FilletFaceSetId,
  type FilletSelectionTarget,
} from '@/core/features/blend/stage-two';
import type { BlendSelectionDisplayItem } from '@/core/features/blend/types';
import type { ViewportSelectionSnapshot } from '../extrude-types';
import { resolvePartialEdgeItemRefs, type ActiveBlendKind } from './blend-feature-helpers';

const DEFAULT_FILLET_SELECTION_TARGET: FilletSelectionTarget = 'faces';
const DEFAULT_CHAMFER_SELECTION_TARGET: ChamferSelectionTarget = 'faces';
type BlendCommandKind = Exclude<ActiveBlendKind, null>;
type ChamferFaceSetId = 'faceSet1' | 'faceSet2';

type FilletUiState = {
  active: boolean;
  editingFeatureId: string | null;
  definition: FilletFeatureDefinition | null;
  upstreamBodyObjectId: string | null;
  items: BlendSelectionDisplayItem[];
  activeFaceSetId: FilletFaceSetId | null;
  selectionTarget: FilletSelectionTarget;
  partialReferenceTarget: BlendPartialReferenceTarget | null;
  selectedItemRef: string | null;
  selectedVariablePointKey: string | null;
  canSubmit: boolean;
  previewError: string | null;
  previewGuides: BlendPreviewGuide[];
};

type ChamferUiState = {
  active: boolean;
  editingFeatureId: string | null;
  definition: ChamferFeatureDefinition | null;
  upstreamBodyObjectId: string | null;
  items: BlendSelectionDisplayItem[];
  activeFaceSetId: ChamferFaceSetId;
  selectionTarget: ChamferSelectionTarget;
  partialReferenceTarget: BlendPartialReferenceTarget | null;
  selectedItemRef: string | null;
  canSubmit: boolean;
  previewError: string | null;
  previewGuides: BlendPreviewGuide[];
};

interface BlendFeatureActionContext {
  editingFeatureId: Ref<string | null>;
  filletDefinition: Ref<FilletFeatureDefinition>;
  chamferDefinition: Ref<ChamferFeatureDefinition>;
  filletItems: Ref<BlendSelectionDisplayItem[]>;
  chamferItems: Ref<BlendSelectionDisplayItem[]>;
  filletActiveFaceSetId: Ref<FilletFaceSetId | null>;
  chamferActiveFaceSetId: Ref<ChamferFaceSetId>;
  filletSelectionTarget: Ref<FilletSelectionTarget>;
  chamferSelectionTarget: Ref<ChamferSelectionTarget>;
  filletPartialReferenceTarget: Ref<BlendPartialReferenceTarget | null>;
  chamferPartialReferenceTarget: Ref<BlendPartialReferenceTarget | null>;
  filletSelectedItemRef: Ref<string | null>;
  filletSelectedVariablePointKey: Ref<string | null>;
  chamferSelectedItemRef: Ref<string | null>;
  filletUiState: ComputedRef<FilletUiState>;
  chamferUiState: ComputedRef<ChamferUiState>;
  startCommand: (kind: BlendCommandKind) => void;
  startEdit: (kind: BlendCommandKind, featureId: string) => Promise<boolean>;
  handleBlendSelectionChange: (snapshot?: ViewportSelectionSnapshot) => Promise<boolean>;
  resetSession: (restoreFeature: boolean) => void;
  submit: (kind: BlendCommandKind) => Promise<void>;
  syncFillet: () => void;
  syncChamfer: () => void;
  syncVariableRadiusControlPoints: () => void;
  syncSelectionMode: () => void;
  schedulePreview: (force?: boolean) => void;
  selectFilletItem: (persistentRef: string) => void;
  removeFilletItem: (persistentRef: string) => void;
  selectChamferItem: (persistentRef: string) => void;
  removeChamferItem: (persistentRef: string) => void;
  message: MessageApi;
}

/**
 * 构建圆角/倒角命令对外动作 API。
 *
 * 设计说明：
 * 1. `useBlendFeature` 负责持有状态与核心流程；
 * 2. 本文件只把这些状态与流程组装成外部调用的动作对象；
 * 3. 拆分后外部 API 不变，但主 composable 不再继续膨胀。
 *
 * @param context 圆角/倒角命令上下文。
 * @returns 对外动作 API。
 */
export function createBlendFeatureActions(context: BlendFeatureActionContext) {
  const {
    editingFeatureId,
    filletDefinition,
    chamferDefinition,
    filletItems,
    chamferItems,
    filletActiveFaceSetId,
    chamferActiveFaceSetId,
    filletSelectionTarget,
    chamferSelectionTarget,
    filletPartialReferenceTarget,
    chamferPartialReferenceTarget,
    filletSelectedItemRef,
    filletSelectedVariablePointKey,
    chamferSelectedItemRef,
    filletUiState,
    chamferUiState,
    startCommand,
    startEdit,
    handleBlendSelectionChange,
    resetSession,
    submit,
    syncFillet,
    syncChamfer,
    syncVariableRadiusControlPoints,
    syncSelectionMode,
    schedulePreview,
    selectFilletItem,
    removeFilletItem,
    selectChamferItem,
    removeChamferItem,
    message,
  } = context;
  return {
    filletUiState,
    chamferUiState,
    startFilletCommand: () => startCommand('fillet'),
    startChamferCommand: () => startCommand('chamfer'),
    startEditingFilletFeature: (featureId: string) => startEdit('fillet', featureId),
    startEditingChamferFeature: (featureId: string) => startEdit('chamfer', featureId),
    handleBlendSelectionChange,
    cancelBlend: () => resetSession(true),
    submitFillet: () => submit('fillet'),
    submitChamfer: () => submit('chamfer'),
    setFilletType: (type: FilletType) => {
      const next = normalizeFilletDefinitionForTypeSwitch(clonePlainData(filletDefinition.value), type);
      next.items = type === 'face'
        ? []
        : (next.items ?? []).filter((item) => (type === 'constant' ? item.kind !== 'vertex' : item.kind === 'edge'));
      next.faceSets = type === 'face' ? (next.faceSets ?? []) : [];
      next.partialEdges = type === 'constant'
        ? ensurePartialEdgeParams(
          next.partialEdges,
          resolvePartialEdgeItemRefs(next.items ?? []),
        )
        : [];
      if (type === 'variable') {
        const instanceCount = next.variableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT;
        next.variableInstanceCount = instanceCount;
        next.variableMode = next.variableMode ?? 'symmetric';
        next.variableProfile = next.variableMode === 'asymmetric' ? 'elliptic' : 'circular';
        next.variableTransition = next.variableTransition ?? 'smooth';
        next.variablePoints = ensureVariableRadiusEndpointPoints(
          next.variablePoints,
          resolvePartialEdgeItemRefs(next.items ?? []),
          instanceCount,
          next.radius ?? 10,
          next.radiusExpression ?? null,
        );
      }
      filletDefinition.value = normalizeConstantFilletDefinition(next, next.items ?? []);
      filletActiveFaceSetId.value = resolveDefaultFilletFaceSetId(type);
      filletSelectionTarget.value = DEFAULT_FILLET_SELECTION_TARGET;
      filletSelectedItemRef.value = (next.items ?? []).some((item) => item.persistentRef === filletSelectedItemRef.value)
        ? filletSelectedItemRef.value
        : (next.items ?? [])[0]?.persistentRef ?? null;
      filletSelectedVariablePointKey.value = null;
      syncVariableRadiusControlPoints();
      syncFillet();
      syncSelectionMode();
      schedulePreview(true);
      message.info(describeBlendSelectionTarget('fillet', type));
    },
    selectFilletItem,
    removeFilletItem,
    highlightFilletItem: (_persistentRef: string | null) => {},
    setFilletAsymmetricRadiiFlipped: (value: boolean) => {
      filletDefinition.value = { ...filletDefinition.value, asymmetricRadiiFlipped: value };
      syncFillet();
      schedulePreview();
    },
    selectFilletVariablePoint: (key: string) => {
      const candidate = parseVariableRadiusInstanceCandidateKey(key);
      if (candidate) {
        const result = upsertVariableRadiusInstancePointWithSelection(
          filletDefinition.value.variablePoints,
          candidate.itemRef,
          candidate.instanceIndex,
          filletDefinition.value.variableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
        );
        if (filletDefinition.value.items?.some((item) => item.persistentRef === candidate.itemRef && item.kind === 'edge')) {
          filletSelectedItemRef.value = candidate.itemRef;
        }
        filletDefinition.value = {
          ...filletDefinition.value,
          variablePoints: result.points,
        };
        filletSelectedVariablePointKey.value = result.selectedKey;
        syncVariableRadiusControlPoints();
        syncFillet();
        schedulePreview(true);
        return;
      }
      const itemRef = key.slice(0, key.lastIndexOf(':'));
      if (filletDefinition.value.items?.some((item) => item.persistentRef === itemRef && item.kind === 'edge')) {
        filletSelectedItemRef.value = itemRef;
      }
      /**
       * 只有场景候选点 I# 可以触发 upsert。
       * “半径点”列表里的 P# 是已经存在的真实控制点 key，点击它只能改变当前选中项，
       * 不能再次按 instanceIndex 插入，否则 P1 会被错误新增为 P2。
       */
      filletSelectedVariablePointKey.value = key;
      syncVariableRadiusControlPoints();
      syncFillet();
      schedulePreview(true);
    },
    removeFilletVariablePoint: (key: string) => {
      const edgeItemRefs = resolvePartialEdgeItemRefs(filletDefinition.value.items ?? []);
      filletDefinition.value = {
        ...filletDefinition.value,
        variablePoints: removeVariableRadiusInstancePoint(
          filletDefinition.value.variablePoints,
          key,
          edgeItemRefs,
          filletDefinition.value.variableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
          filletDefinition.value.radius ?? 10,
          filletDefinition.value.radiusExpression ?? null,
        ),
      };
      filletSelectedVariablePointKey.value = resolveSelectedVariableRadiusPointKey(
        filletDefinition.value.variablePoints,
        filletSelectedVariablePointKey.value === key ? null : filletSelectedVariablePointKey.value,
      );
      syncVariableRadiusControlPoints();
      syncFillet();
      schedulePreview(true);
    },
    removeAllFilletVariableInstancePoints: () => {
      const edgeItemRefs = resolvePartialEdgeItemRefs(filletDefinition.value.items ?? []);
      filletDefinition.value = {
        ...filletDefinition.value,
        variablePoints: removeAllVariableRadiusInstancePoints(
          filletDefinition.value.variablePoints,
          edgeItemRefs,
          filletDefinition.value.variableInstanceCount ?? DEFAULT_VARIABLE_FILLET_INSTANCE_COUNT,
          filletDefinition.value.radius ?? 10,
          filletDefinition.value.radiusExpression ?? null,
        ),
      };
      filletSelectedVariablePointKey.value = resolveSelectedVariableRadiusPointKey(
        filletDefinition.value.variablePoints,
        null,
      );
      syncVariableRadiusControlPoints();
      syncFillet();
      schedulePreview(true);
    },
    assignFilletRadiusToAllPoints: (
      value?: number,
      expression: string | null = null,
      radius2Value?: number,
      radius2Expression: string | null = null,
    ) => {
      const radius = typeof value === 'number' && Number.isFinite(value) && value > 0
        ? value
        : filletDefinition.value.radius ?? 10;
      const radius2 = typeof radius2Value === 'number' && Number.isFinite(radius2Value) && radius2Value > 0
        ? radius2Value
        : undefined;
      filletDefinition.value = {
        ...filletDefinition.value,
        variablePoints: assignVariableRadiusToAllPoints(
          filletDefinition.value.variablePoints,
          radius,
          expression,
          radius2,
          radius2Expression,
        ),
      };
      syncFillet();
      schedulePreview(true);
    },
    assignFilletRadiusToUnassignedPoints: () => {
      const radius = filletDefinition.value.radius ?? 10;
      filletDefinition.value = {
        ...filletDefinition.value,
        variablePoints: (filletDefinition.value.variablePoints ?? []).map((point) => (
          point.radius === null || !Number.isFinite(point.radius)
            ? { ...point, radius, expression: null }
            : point
        )),
      };
      syncFillet();
      schedulePreview(true);
    },
    setFilletSelectionTarget: (target: FilletSelectionTarget) => {
      filletSelectionTarget.value = target;
      syncSelectionMode();
    },
    setFilletSelectThroughFaces: (value: boolean) => {
      filletDefinition.value = { ...filletDefinition.value, selectThroughFaces: value };
      syncFillet();
      syncSelectionMode();
      schedulePreview(true);
    },
    setFilletKeepFeatures: (value: boolean) => {
      filletDefinition.value = { ...filletDefinition.value, keepFeatures: value };
      syncFillet();
      schedulePreview(true);
    },
    setFilletRadius: (value: number, expression: string | null = null) => {
      filletDefinition.value = { ...filletDefinition.value, radius: value, radiusExpression: expression };
      syncFillet();
      schedulePreview();
    },
    setFilletRadius2: (value: number, expression: string | null = null) => {
      filletDefinition.value = { ...filletDefinition.value, radius2: value, radius2Expression: expression };
      syncFillet();
      schedulePreview();
    },
    setFilletEdgeFilletMode: (value: FilletFeatureDefinition['edgeFilletMode']) => {
      filletDefinition.value = normalizeConstantFilletDefinition({
        ...filletDefinition.value,
        edgeFilletMode: isConstantFilletEdgeModeLocked(filletItems.value) ? 'symmetric' : value,
      }, filletItems.value);
      syncFillet();
      syncSelectionMode();
      schedulePreview(true);
    },
    setFilletOverflowType: (value: FilletFeatureDefinition['overflowType']) => {
      filletDefinition.value = { ...filletDefinition.value, overflowType: value };
      syncFillet();
      schedulePreview(true);
    },
    setFilletVariablePointRadius: (
      key: string,
      value: number,
      expression: string | null = null,
      slot: VariableRadiusValueSlot = 'radius1',
    ) => {
      filletDefinition.value = {
        ...filletDefinition.value,
        variablePoints: updateVariableRadiusPointRadius(
          filletDefinition.value.variablePoints,
          key,
          value,
          expression,
          slot,
        ),
      };
      filletSelectedVariablePointKey.value = key;
      syncFillet();
      schedulePreview();
    },
    setFilletVariableInstanceCount: (count: number) => {
      const instanceCount = clampVariableRadiusInstanceCount(count);
      filletDefinition.value = {
        ...filletDefinition.value,
        variableInstanceCount: instanceCount,
        variablePoints: ensureVariableRadiusEndpointPoints(
          filletDefinition.value.variablePoints,
          resolvePartialEdgeItemRefs(filletDefinition.value.items ?? []),
          instanceCount,
          filletDefinition.value.radius ?? 10,
          filletDefinition.value.radiusExpression ?? null,
        ),
      };
      syncVariableRadiusControlPoints();
      syncFillet();
      schedulePreview(true);
    },
    setFilletVariableMode: (mode: NonNullable<FilletFeatureDefinition['variableMode']>) => {
      filletDefinition.value = {
        ...filletDefinition.value,
        variableMode: mode,
        variableProfile: mode === 'asymmetric' ? 'elliptic' : 'circular',
        variablePoints: mode === 'asymmetric'
          ? (filletDefinition.value.variablePoints ?? []).map((point) => ({
            ...point,
            radius2: typeof point.radius2 === 'number' && Number.isFinite(point.radius2) && point.radius2 > 0
              ? point.radius2
              : point.radius ?? filletDefinition.value.radius ?? 10,
            expression2: point.expression2 ?? point.expression ?? filletDefinition.value.radiusExpression ?? null,
          }))
          : filletDefinition.value.variablePoints,
      };
      syncFillet();
      schedulePreview(true);
    },
    setFilletVariableTransition: (transition: NonNullable<FilletFeatureDefinition['variableTransition']>) => {
      filletDefinition.value = { ...filletDefinition.value, variableTransition: transition };
      syncFillet();
      schedulePreview(true);
    },
    setFilletFaceFilletMode: (value: FilletFeatureDefinition['faceFilletMode']) => {
      filletDefinition.value = normalizeFilletDefinitionForFaceFilletMode(
        filletDefinition.value,
        value ?? 'symmetric',
      );
      syncFillet();
      syncSelectionMode();
      schedulePreview(true);
    },
    setFilletChordWidth: (value: number, expression: string | null = null) => {
      filletDefinition.value = {
        ...filletDefinition.value,
        chordWidth: value,
        chordWidthExpression: expression,
      };
      syncFillet();
      schedulePreview();
    },
    setFilletFaceFilletRadiiFlipped: (value: boolean) => {
      filletDefinition.value = { ...filletDefinition.value, faceFilletRadiiFlipped: value };
      syncFillet();
      schedulePreview(true);
    },
    setFilletPartialEdgeParametersEnabled: (value: boolean) => {
      filletDefinition.value = setPartialEdgeParametersEnabled(
        filletDefinition.value,
        resolvePartialEdgeItemRefs(filletDefinition.value.items ?? []),
        value,
      );
      if (!value && filletSelectionTarget.value === 'partial-edge-reference') {
        filletSelectionTarget.value = DEFAULT_FILLET_SELECTION_TARGET;
        filletPartialReferenceTarget.value = null;
      }
      syncFillet();
      syncSelectionMode();
      schedulePreview(true);
    },
    setFilletPartialCondition: (itemRef: string, side: 'start' | 'end', condition: BlendPartialCondition) => {
      filletDefinition.value = updatePartialEdgeCondition(filletDefinition.value, itemRef, side, condition);
      const activation = resolvePartialReferenceActivationAfterConditionChange({
        currentTarget: filletPartialReferenceTarget.value,
        itemRef,
        side,
        condition,
        referenceSelectionTarget: 'partial-edge-reference' as FilletSelectionTarget,
        defaultSelectionTarget: DEFAULT_FILLET_SELECTION_TARGET,
      });
      filletPartialReferenceTarget.value = activation.referenceTarget;
      filletSelectionTarget.value = activation.selectionTarget;
      if (activation.shouldSyncSelectionMode) {
        syncSelectionMode();
      }
      syncFillet();
      schedulePreview();
    },
    setFilletPartialValue: (itemRef: string, side: 'start' | 'end', value: number, expression: string | null = null) => {
      filletDefinition.value = updatePartialEdgeValue(filletDefinition.value, itemRef, side, value, expression);
      syncFillet();
      schedulePreview();
    },
    setFilletPartialReferenceTarget: (itemRef: string, side: 'start' | 'end', active: boolean) => {
      filletPartialReferenceTarget.value = active ? { itemRef, side } : null;
      filletSelectionTarget.value = active ? 'partial-edge-reference' : DEFAULT_FILLET_SELECTION_TARGET;
      syncSelectionMode();
    },
    clearFilletPartialReference: (itemRef: string, side: 'start' | 'end') => {
      filletDefinition.value = clearPartialEdgeReference(filletDefinition.value, itemRef, side);
      syncFillet();
      schedulePreview();
    },
    setFilletFaceSetTarget: (setId: FilletFaceSetId) => {
      filletActiveFaceSetId.value = setId;
    },
    setChamferType: (type: ChamferType) => {
      if (
        editingFeatureId.value
        && !canSwitchChamferTypeDuringEdit({
          currentType: chamferDefinition.value.type,
          nextType: type,
        })
      ) {
        message.warning('当前编辑中的倒角不能在顶点倒角与其它倒角之间切换，请新建对应类型的倒角特征。');
        return;
      }

      const nextDefinition = normalizeChamferDefinitionForTypeSwitch(
        clonePlainData(chamferDefinition.value),
        type,
      );
      chamferDefinition.value = {
        ...nextDefinition,
        partialEdges: type === 'offset-face'
          ? ensurePartialEdgeParams(
            nextDefinition.partialEdges,
            resolvePartialEdgeItemRefs(nextDefinition.items ?? []),
          )
          : [],
      };
      chamferSelectionTarget.value = DEFAULT_CHAMFER_SELECTION_TARGET;
      chamferSelectedItemRef.value = null;
      syncChamfer();
      syncSelectionMode();
      schedulePreview(true);
      message.info(describeBlendSelectionTarget('chamfer', type));
    },
    selectChamferItem,
    removeChamferItem,
    highlightChamferItem: (_persistentRef: string | null) => {},
    setChamferTangentPropagation: (value: boolean) => {
      void value;
      chamferDefinition.value = { ...chamferDefinition.value, tangentPropagation: false };
      syncChamfer();
      schedulePreview(true);
    },
    setChamferFlipDirection: (value: boolean) => {
      chamferDefinition.value = { ...chamferDefinition.value, flipDirection: value };
      syncChamfer();
      schedulePreview();
    },
    setChamferEqualDistance: (value: boolean) => {
      const currentDistance = chamferDefinition.value.distance ?? DEFAULT_CHAMFER_DISTANCE;
      const currentVertexDistances = chamferDefinition.value.vertexDistances ?? [
        currentDistance,
        currentDistance,
        currentDistance,
      ];
      chamferDefinition.value = value
        ? {
          ...chamferDefinition.value,
          equalDistance: true,
          distance: currentVertexDistances[0] ?? currentDistance,
        }
        : {
          ...chamferDefinition.value,
          equalDistance: false,
          vertexDistances: currentVertexDistances,
        };
      syncChamfer();
      schedulePreview();
    },
    setChamferSelectThroughFaces: (value: boolean) => {
      void value;
      chamferDefinition.value = { ...chamferDefinition.value, selectThroughFaces: false };
      syncChamfer();
      syncSelectionMode();
      schedulePreview(true);
    },
    setChamferKeepFeatures: (value: boolean) => {
      void value;
      chamferDefinition.value = { ...chamferDefinition.value, keepFeatures: true };
      syncChamfer();
      schedulePreview(true);
    },
    setChamferSymmetric: (value: boolean) => {
      chamferDefinition.value = normalizeOffsetFaceChamferDefinition({
        ...chamferDefinition.value,
        symmetric: value,
      });
      syncChamfer();
      syncSelectionMode();
      schedulePreview();
    },
    setChamferDistance: (value: number, expression: string | null = null) => {
      chamferDefinition.value = { ...chamferDefinition.value, distance: value, distanceExpression: expression };
      syncChamfer();
      schedulePreview();
    },
    setChamferDistance2: (value: number, expression: string | null = null) => {
      chamferDefinition.value = { ...chamferDefinition.value, distance2: value, distance2Expression: expression };
      syncChamfer();
      schedulePreview();
    },
    setChamferWidth: (value: number, expression: string | null = null) => {
      chamferDefinition.value = { ...chamferDefinition.value, width: value, widthExpression: expression };
      syncChamfer();
      schedulePreview();
    },
    setChamferAngleDeg: (value: number, expression: string | null = null) => {
      chamferDefinition.value = { ...chamferDefinition.value, angleDeg: value, angleExpression: expression };
      syncChamfer();
      schedulePreview();
    },
    setChamferVertexDistance: (index: number, value: number, expression: string | null = null) => {
      const distances = [...(chamferDefinition.value.vertexDistances ?? [
        DEFAULT_CHAMFER_DISTANCE,
        DEFAULT_CHAMFER_DISTANCE,
        DEFAULT_CHAMFER_DISTANCE,
      ])] as [number, number, number];
      const expressions = [...(chamferDefinition.value.vertexDistanceExpressions ?? [null, null, null])] as [string | null, string | null, string | null];
      distances[index] = value;
      expressions[index] = expression;
      chamferDefinition.value = {
        ...chamferDefinition.value,
        vertexDistances: distances,
        vertexDistanceExpressions: expressions,
      };
      syncChamfer();
      schedulePreview();
    },
    setChamferPartialEdgeParametersEnabled: (value: boolean) => {
      chamferDefinition.value = setPartialEdgeParametersEnabled(
        chamferDefinition.value,
        resolvePartialEdgeItemRefs(chamferDefinition.value.items ?? []),
        value,
      );
      if (!value && chamferSelectionTarget.value === 'partial-edge-reference') {
        chamferSelectionTarget.value = DEFAULT_CHAMFER_SELECTION_TARGET;
        chamferPartialReferenceTarget.value = null;
      }
      syncChamfer();
      syncSelectionMode();
      schedulePreview(true);
    },
    setChamferPartialCondition: (itemRef: string, side: 'start' | 'end', condition: BlendPartialCondition) => {
      chamferDefinition.value = updatePartialEdgeCondition(chamferDefinition.value, itemRef, side, condition);
      const activation = resolvePartialReferenceActivationAfterConditionChange({
        currentTarget: chamferPartialReferenceTarget.value,
        itemRef,
        side,
        condition,
        referenceSelectionTarget: 'partial-edge-reference' as ChamferSelectionTarget,
        defaultSelectionTarget: DEFAULT_CHAMFER_SELECTION_TARGET,
      });
      chamferPartialReferenceTarget.value = activation.referenceTarget;
      chamferSelectionTarget.value = activation.selectionTarget;
      if (activation.shouldSyncSelectionMode) {
        syncSelectionMode();
      }
      syncChamfer();
      schedulePreview();
    },
    setChamferPartialValue: (itemRef: string, side: 'start' | 'end', value: number, expression: string | null = null) => {
      chamferDefinition.value = updatePartialEdgeValue(chamferDefinition.value, itemRef, side, value, expression);
      syncChamfer();
      schedulePreview();
    },
    setChamferPartialReferenceTarget: (itemRef: string, side: 'start' | 'end', active: boolean) => {
      chamferPartialReferenceTarget.value = active ? { itemRef, side } : null;
      chamferSelectionTarget.value = active ? 'partial-edge-reference' : DEFAULT_CHAMFER_SELECTION_TARGET;
      syncSelectionMode();
    },
    clearChamferPartialReference: (itemRef: string, side: 'start' | 'end') => {
      chamferDefinition.value = clearPartialEdgeReference(chamferDefinition.value, itemRef, side);
      syncChamfer();
      schedulePreview();
    },
    setChamferFaceFaceMode: (value: ChamferFeatureDefinition['faceFaceMode']) => {
      const nextDistance = chamferDefinition.value.distance ?? 10;
      chamferDefinition.value = normalizeChamferDefinitionForFaceFaceMode({
        ...chamferDefinition.value,
        faceFaceMode: value,
        symmetric: value === 'asymmetric' ? false : true,
        distance2: value === 'asymmetric'
          ? (chamferDefinition.value.distance2 ?? nextDistance)
          : chamferDefinition.value.distance2,
        width: value === 'chord-width'
          ? (chamferDefinition.value.width ?? nextDistance)
          : chamferDefinition.value.width,
      }, value ?? 'symmetric');
      syncChamfer();
      syncSelectionMode();
      schedulePreview(true);
    },
    setChamferFaceSetTarget: (setId: 'faceSet1' | 'faceSet2') => {
      chamferActiveFaceSetId.value = setId;
    },
    setChamferSelectionTarget: (target: ChamferSelectionTarget) => {
      chamferSelectionTarget.value = target;
      syncSelectionMode();
    },
  };
}
